<template>
  <section id="team" class="team">

    <div class="container">

      <div class="section-header">

        <h2>
          {{ titlesLocale.first  }}

          <span class="gradient-text">
            {{ titlesLocale.second }}
          </span>
        </h2>

        <p>
          {{ titlesLocale.description }}
        </p>

      </div>

      <div class="team-grid">

        <div
          v-for="membro in equipe"
          :key="membro.nome"
          class="team-card"
        >

          <div class="card-glow"></div>

          <div
            class="avatar"
            :class="membro.avatarStyle"
          >

            <img
              :src="membro.foto"
              :alt="`Foto ${membro.nome}`"
              class="avatar-img"
              loading="lazy"
            />

          </div>

          <h3>
            {{ membro.nome }}
          </h3>

          <p class="role">
            {{ membro[locale].cargo }}
          </p>

          <p class="description">
            {{ membro[locale].sobre }}
          </p>

          <div class="skills">

            <span
              v-for="skill in membro.skills"
              :key="skill"
            >
              {{ skill }}
            </span>

          </div>

          <div class="social">

            <a
              v-for="(rede, index) in membro.redes"
              :key="index"
              :href="rede.link"
              target="_blank"
              rel="noopener noreferrer"
              :title="rede.nome"
            >

              <img
                :src="rede.icon"
                :alt="rede.nome"
                class="social-icon"
              />

            </a>

          </div>

        </div>

      </div>

    </div>

  </section>
</template>

<script setup>
import { equipe, titles } from "@/data/equipe"
import { computed } from "vue"
import { useI18n } from "vue-i18n"

const { locale } = useI18n()

const titlesLocale = computed(() => {

  if (locale.value === "pt") {
    return titles.pt
  }

  return titles.en

})
</script>

<style scoped>
.team {
  padding: 30px 20px;
  background: var(--bg-primario);
  scroll-margin-top: 90px;
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
}

.section-header h2 {
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(24px, 4vw, 48px);
}

.section-header p {
  color: var(--cor-texto-suave);
  font-size: 16px;
  max-width: 600px;
  margin: 0 auto 20px;
}

.team-grid {
  display: flex;
  justify-content: space-around;
  flex-direction: row;
  gap: 50px;
  margin: 0 auto;
}

.team-grid h3 {
  font-size: 26px;
  letter-spacing: 1.5px;
}

.team-card {
  position: relative;
  width: 420px;
  padding: 20px 10px;
  border-radius: 20px;
  background: var(--bg-destaque);
  border: 1px solid var(--card-base);
  text-align: center;
  overflow: hidden;
}

.card-glow {
  display: none;
}

.avatar {
  width: 150px;
  height: 150px;
  margin: 0 auto 25px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: glow 2s infinite alternate;
  overflow: hidden;
}

.avatar-1 {
  background: linear-gradient(
    135deg,
    var(--card-base),
    var(--card-destaque)
  );
}

.avatar-2 {
  background: linear-gradient(
    135deg,
    var(--card-destaque),
    var(--card-secundaria)
  );
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.role {
  color: var(--card-destaque);
  margin-bottom: 15px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1.5px;
}

.description {
  color: var(--cor-texto-secundario);
  margin-bottom: 25px;
  font-size: 13px;
  letter-spacing: 1.5px;
}

.skills {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin-bottom: 25px;
}

.skills span {
  background: var(--card-base);
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  letter-spacing: 1.5px;
}

.social {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.social a {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  transition: 0.3s ease;
}

.social a:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 18px var(--card-sombra);
}

.social-icon {
  width: 45px;
  height: 45px;
  object-fit: contain;
  border-radius: 12px;
}

@keyframes glow {

  from {
    box-shadow: 0 0 10px var(--card-destaque);
  }

  to {
    box-shadow: 0 0 30px var(--card-destaque);
  }

}

@media (max-width: 1280px) {

  .team-grid {
    gap: 20px;
    justify-content: center;
  }

  .team-card {
    width: 350px;
    padding: 20px 15px;
  }

  .team-grid h3 {
    font-size: 22px;
  }

  .description {
    font-size: 12px;
  }

  .skills {
    gap: 10px;
  }

}

@media (max-width: 800px) {

  .section-header {
    margin-bottom: 20px;
  }

  .section-header p {
    color: var(--cor-texto-suave);
    font-size: 12px;
    max-width: 400px;
  }

  .team-grid {
    justify-content: center;
    gap: 10px;
  }

  .team-grid h3 {
    font-size: 16px;
    margin-bottom: 4px;
  }

  .team-card {
    width: 200px;
    padding: 10px 5px;
    border-radius: 16px;
  }

  .avatar {
    width: 100px;
    height: 100px;
    margin: 0 auto 15px;
  }

  .role {
    color: var(--card-destaque);
    margin-bottom: 10px;
    font-size: 11px;
    font-weight: 500;
  }

  .description {
    margin-bottom: 15px;
    font-size: 11px;
  }

  .skills {
    gap: 6px;
    margin-bottom: 15px;
  }

  .skills span {
    padding: 4px 8px;
    font-size: 9px;
  }

  .social {
    gap: 5px;
  }

  .social-icon {
    width: 35px;
    height: 35px;
    object-fit: contain;
    border-radius: 12px;
  }

}
</style>